import { useEffect, useRef } from 'react'
import {
  ASSUMPTION_DOSSIER,
  ASSUMPTIONS_OUTSTANDING,
  bestAchievable,
} from '../assumptions/india'
import type { Assumption, AssumptionStatus } from '../assumptions/india'

/**
 * "Why these numbers" — the whole dossier, read-only.
 *
 * This is the credibility surface. Finance-Reddit attacks the assumptions before
 * it attacks anything else, and the only winning move is to have published them
 * first, with an honest label on each. A number shown with its reasoning and a
 * frank "this is our estimate" survives that argument; the same number hidden
 * behind a headline does not.
 *
 * It also does compliance work. The CCPA Guidelines, 2022 require that a claim
 * stated as resting on research indicate the source and date of that research —
 * this is where that lives for the probability on the front screen.
 *
 * Read-only in F1 deliberately. Editing lands in F3, once the launch has shown
 * which numbers people actually contest. Every extra dial is another way to
 * produce a flattering wrong answer, so they should be added on evidence rather
 * than in anticipation.
 */

interface AssumptionsPanelProps {
  open: boolean
  onClose: () => void
}

export function AssumptionsPanel({ open, onClose }: AssumptionsPanelProps) {
  const closeRef = useRef<HTMLButtonElement | null>(null)

  // Escape closes, and focus moves in on open — this is a modal surface, and one
  // that traps keyboard users is worse than no panel at all.
  useEffect(() => {
    if (!open) return
    closeRef.current?.focus()

    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open, onClose])

  if (!open) return null

  const sourced = ASSUMPTION_DOSSIER.length - ASSUMPTIONS_OUTSTANDING.length

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center sm:items-center"
      role="dialog"
      aria-modal="true"
      aria-labelledby="assumptions-title"
    >
      <button
        type="button"
        aria-label="Close assumptions"
        onClick={onClose}
        className="absolute inset-0 bg-[color:var(--c-bg-void)]/70 backdrop-blur-sm"
      />

      <div className="relative flex max-h-[88dvh] w-full max-w-2xl flex-col rounded-t-[var(--radius-xl)] border border-[color:var(--c-border)] bg-[color:var(--c-bg-surface)] shadow-[var(--shadow-lg)] sm:rounded-[var(--radius-xl)]">
        <header className="flex items-start justify-between gap-4 border-b border-[color:var(--c-border-subtle)] px-5 py-4">
          <div>
            <h2
              id="assumptions-title"
              className="text-[length:var(--text-title)] text-[color:var(--c-text-primary)]"
            >
              Why these numbers
            </h2>
            <p className="mt-1 text-[length:var(--text-label)] text-[color:var(--c-text-tertiary)]">
              {sourced} of {ASSUMPTION_DOSSIER.length} are as well-founded as they can be.
              The rest are our estimates, marked as such.
            </p>
          </div>
          <button
            ref={closeRef}
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="shrink-0 rounded-[var(--radius-full)] text-[color:var(--c-text-tertiary)] transition-colors duration-[var(--dur-fast)] hover:bg-[color:var(--c-bg-hover)] hover:text-[color:var(--c-text-primary)]"
            style={{ width: 'var(--touch-target)', height: 'var(--touch-target)' }}
          >
            ✕
          </button>
        </header>

        <div className="overflow-y-auto px-5 py-4">
          <p className="mb-4 text-[length:var(--text-label)] leading-relaxed text-[color:var(--c-text-secondary)]">
            A projection is only as good as what goes into it. Most of these cannot be
            "verified" in any honest sense — an expected return is a claim about the future,
            and no citation settles it. So each one says what kind of number it is, and how
            well-founded it is right now.
          </p>

          <ul className="flex flex-col gap-2">
            {ASSUMPTION_DOSSIER.map((assumption) => (
              <AssumptionRow key={assumption.label} assumption={assumption} />
            ))}
          </ul>
        </div>
      </div>
    </div>
  )
}

function AssumptionRow({ assumption }: { assumption: Assumption<number> }) {
  const { label, value, kind, status, basis, note, source } = assumption

  return (
    <li className="rounded-[var(--radius-md)] border border-[color:var(--c-border-subtle)]">
      {/* Native <details>: keyboard and screen-reader behaviour for free, and the
          panel stays scannable with fifteen entries instead of becoming an essay. */}
      <details>
        <summary className="flex cursor-pointer list-none items-center justify-between gap-3 px-3 py-2.5">
          <span className="text-[length:var(--text-label)] text-[color:var(--c-text-primary)]">
            {label}
          </span>
          <span className="flex shrink-0 items-center gap-2">
            <span className="numeral text-[length:var(--text-label)] text-[color:var(--c-text-primary)]">
              {formatValue(label, value)}
            </span>
            <StatusBadge status={status} />
          </span>
        </summary>

        <div className="flex flex-col gap-2 border-t border-[color:var(--c-border-subtle)] px-3 py-3">
          <Field
            term={kind === 'data' ? 'Where this is verified' : 'Measured against'}
            detail={basis}
          />
          <Field term="Why it is contestable" detail={note} />
          {source !== undefined && <Field term="Source" detail={source} />}
        </div>
      </details>
    </li>
  )
}

function Field({ term, detail }: { term: string; detail: string }) {
  return (
    <div>
      <p className="text-[length:var(--text-micro)] uppercase tracking-[var(--tracking-label)] text-[color:var(--c-text-tertiary)]">
        {term}
      </p>
      {/* whitespace-pre-line so the paragraph breaks authored into these strings
          survive — several of the notes carry an argument in two halves. */}
      <p className="whitespace-pre-line text-[length:var(--text-micro)] leading-relaxed text-[color:var(--c-text-secondary)]">
        {detail}
      </p>
    </div>
  )
}

/**
 * The honest label. "Unverified" is deliberately rendered as "our estimate" —
 * it reads as a confession rather than a defect, which is what it is, and it is
 * what someone scanning the panel actually needs to know.
 */
function StatusBadge({ status }: { status: AssumptionStatus }) {
  const style: Record<AssumptionStatus, { text: string; token: string }> = {
    verified: { text: 'verified', token: '--c-positive' },
    calibrated: { text: 'calibrated', token: '--c-fan-median' },
    unverified: { text: 'our estimate', token: '--c-caution' },
  }
  const { text, token } = style[status]

  return (
    <span
      className="rounded-[var(--radius-full)] px-2 py-0.5 text-[length:var(--text-micro)]"
      style={{
        color: `var(${token})`,
        border: `1px solid color-mix(in oklch, var(${token}) 35%, transparent)`,
      }}
    >
      {text}
    </span>
  )
}

/** Correlations are plain decimals; everything else in the dossier is a rate. */
function formatValue(label: string, value: number): string {
  if (label.includes('↔')) return value.toFixed(2)
  if (label.includes('multiple')) return `${value}x`
  if (label.includes('persistence')) return value.toFixed(2)
  // A statutory rupee threshold, not a rate. Shown exactly, with Indian digit
  // grouping: ₹1,25,000 is the figure in the Act, and the compact formatter would
  // round it to "₹1.3 L" — misquoting a statute on the one screen built to survive
  // being argued with. Without this branch it falls through to the percentage
  // default and renders as "12500000%".
  if (label.includes('exemption')) return `₹${value.toLocaleString('en-IN')}`
  // One decimal unless the value is a whole number of percent.
  //
  // This used to read `toFixed(pct < 10 ? 1 : 0)`, which was fine while every rate
  // here was either a whole percent or under 10 — and then F7a added 12.5%, which
  // took the `toFixed(0)` branch and rendered the statutory LTCG rate as "13%".
  // A rounded-up tax rate on the screen built to survive being argued with is the
  // worst place in the product to be approximately right, and no test covered
  // this function. Keying on "is it a whole number" instead of "is it small"
  // leaves every pre-existing row's output identical: 12% and 20% stay whole,
  // 4.38% and 8.5% keep their decimal.
  const pct = value * 100
  return `${Number.isInteger(pct) ? pct.toFixed(0) : pct.toFixed(1)}%`
}

export { bestAchievable }
